import React from 'react';
import Viewport from './Viewport';
const {
  DEFAULT_LOAD_LATENCY_MS,
  adaptiveLoadedItemLookahead
} = require('./ScrollLookahead.js');

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function nextAnimationFrame() {
  return new Promise(resolve => {
    window.requestAnimationFrame(() => resolve());
  });
}

class VirtualizedView extends React.Component {
  state = {
    /*
     * The first render must already contain the requested boundary. Waiting
     * for Viewport.scrollToBottom() only scrolls the initially rendered
     * virtual slice; it cannot move that slice to the logical list tail.
     */
    localOffset: this.props.isBottomAligned
      ? Math.max(
          0,
          (this.props.items || []).length -
            Math.max(1, Math.ceil(Number(this.props.windowSize) || 1))
        ) - Math.max(0, Number(this.props.baseOffset) || 0)
      : -Math.max(0, Number(this.props.baseOffset) || 0),
  };

  viewport = null;
  edgeBusy = false;
  scrollSequence = 0;
  loadLatencyMs = {
    before: DEFAULT_LOAD_LATENCY_MS,
    after: DEFAULT_LOAD_LATENCY_MS,
  };

  componentDidMount() {
    if (this.props.onCreate) {
      this.props.onCreate(this);
    }
  }

  getSnapshotBeforeUpdate() {
    return {
      wasBottomLocked: this.isBottomLocked(),
    };
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const windowChanged =
      prevProps.items !== this.props.items ||
      prevProps.baseOffset !== this.props.baseOffset ||
      prevProps.windowSize !== this.props.windowSize;

    if (windowChanged) {
      const shouldPinToBottom =
        this.props.isBottomAligned &&
        snapshot &&
        snapshot.wasBottomLocked;
      const nextOffset = shouldPinToBottom
        ? this.getBottomLocalOffset()
        : this.clampLocalOffset(this.state.localOffset);

      if (nextOffset !== this.state.localOffset) {
        this.setState({ localOffset: nextOffset }, () => {
          if (shouldPinToBottom && this.viewport) {
            this.viewport.scrollToBottom();
          }
        });
      } else if (shouldPinToBottom && this.viewport) {
        this.viewport.scrollToBottom();
      }
    }
  }

  getContentRef = () => {
    return this.viewport ? this.viewport.getContentRef() : null;
  };

  onCreateViewport = viewport => {
    this.viewport = viewport;
  };

  isBottomLocked = () => {
    return Boolean(
      this.props.isBottomAligned &&
      this.viewport &&
      this.viewport.isBottomLocked &&
      this.viewport.isBottomLocked()
    );
  };

  getMaxFinalOffset = () => {
    const { items = [], windowSize } = this.props;
    return Math.max(0, items.length - windowSize);
  };

  getBottomLocalOffset = () => {
    const { baseOffset = 0 } = this.props;
    return this.clampLocalOffset(this.getMaxFinalOffset() - baseOffset);
  };

  getFinalOffset = (localOffset = this.state.localOffset) => {
    const { baseOffset = 0 } = this.props;
    return clamp(
      Math.round(baseOffset + localOffset),
      0,
      this.getMaxFinalOffset(),
    );
  };

  clampLocalOffset = localOffset => {
    const { baseOffset = 0 } = this.props;
    const maxFinalOffset = this.getMaxFinalOffset();

    /*
     * Important:
     * localOffset may be negative after prepending.
     *
     * Example:
     * - User is viewing finalOffset 0.
     * - 20 items are prepended.
     * - DataProvider raises baseOffset to 20 to keep the same content visible.
     * - To scroll into the new prepended items, localOffset must be allowed to
     *   move below 0, down to -baseOffset.
     */
    const minLocalOffset = -baseOffset;
    const maxLocalOffset = maxFinalOffset - baseOffset;

    return clamp(
      Math.round(Number(localOffset) || 0),
      minLocalOffset,
      maxLocalOffset,
    );
  };

  getOverscan = () => {
    const configured = Number(this.props.overscan);
    return Math.max(
      1,
      Math.ceil(
        Number.isFinite(configured) && configured > 0
          ? configured
          : this.props.windowSize / 2
      )
    );
  };

  getStepSize = () => {
    return Math.max(1, Math.floor(this.props.windowSize / 2));
  };

  getLoadedItemLookahead = direction => {
    const range = this.getRenderRange();
    const renderedItemCount = Math.max(0, range.end - range.start);
    const container = this.getContentRef();
    const lookaheadPx = this.viewport && this.viewport.getEdgeLookaheadPx
      ? this.viewport.getEdgeLookaheadPx(direction)
      : 0;
    return adaptiveLoadedItemLookahead({
      lookaheadPx,
      renderedPixelHeight: container && container.scrollHeight,
      renderedItemCount,
      windowSize: this.props.windowSize
    });
  };

  heldRenderRange = null;

  getRenderRange = (localOffset = this.state.localOffset) => {
    const { items = [], windowSize } = this.props;
    const overscan = this.getOverscan();
    const finalOffset = this.getFinalOffset(localOffset);

    /*
     * While the user is away from the bottom, items appended at the tail
     * (e.g. a streaming reply) must not move the rendered slice: the range
     * near the list end is computed against items.length, so every append
     * would otherwise slide the window forward out from under the reader.
     * Hold the previous slice until the offset changes (the user moved),
     * the list shrank (replaced), or the bottom lock takes over (pinned).
     */
    const held = this.heldRenderRange;
    if (
      this.props.isBottomAligned &&
      held &&
      held.length > 0 &&
      held.finalOffset === finalOffset &&
      items.length >= held.length &&
      !this.isBottomLocked()
    ) {
      return { start: held.start, end: held.end, finalOffset };
    }

    const desiredCount = Math.min(items.length, windowSize + overscan * 2);

    let start = Math.max(0, finalOffset - overscan);
    let end = Math.min(items.length, finalOffset + windowSize + overscan);

    /*
     * Near the list end, keep the rendered count stable by shifting start back.
     */
    if (end - start < desiredCount) {
      start = Math.max(0, end - desiredCount);
    }

    this.heldRenderRange = {
      start,
      end,
      finalOffset,
      length: items.length,
    };

    return {
      start,
      end,
      finalOffset,
    };
  };

  isIndexRendered = index => {
    const { start, end } = this.getRenderRange();
    return index >= start && index < end;
  };

  setLocalOffset = localOffset => {
    return new Promise(resolve => {
      const nextOffset = this.clampLocalOffset(localOffset);

      if (nextOffset === this.state.localOffset) {
        resolve(false);
        return;
      }

      this.setState({ localOffset: nextOffset }, () => resolve(true));
    });
  };

  loadMore = async direction => {
    if (!this.props.onLoadMore) {
      return { count: 0 };
    }

    const startedAt = typeof performance !== 'undefined'
      ? performance.now()
      : Date.now();
    const result = await this.props.onLoadMore(direction);
    await nextAnimationFrame();
    const finishedAt = typeof performance !== 'undefined'
      ? performance.now()
      : Date.now();
    const elapsed = Math.max(1, finishedAt - startedAt);
    const previous = this.loadLatencyMs[direction] || DEFAULT_LOAD_LATENCY_MS;

    /*
     * Bias toward a newly observed slowdown immediately, while letting faster
     * samples reduce the budget gradually. A transient slow Firestore page is
     * exactly when the next scroll needs more runway.
     */
    this.loadLatencyMs[direction] = elapsed >= previous
      ? elapsed
      : previous * 0.75 + elapsed * 0.25;

    return result;
  };

  getLoadLatencyMs = direction => {
    return this.loadLatencyMs[direction] || DEFAULT_LOAD_LATENCY_MS;
  };

  handleNearTop = async () => {
    if (this.edgeBusy) return;

    this.edgeBusy = true;
    let shouldLoad = false;

    try {
      const stepSize = this.getStepSize();
      const currentFinalOffset = this.getFinalOffset();

      if (currentFinalOffset > 0) {
        await this.setLocalOffset(this.state.localOffset - stepSize);
      }

      /*
       * Prefetch before we fully exhaust the current item array.
       */
      shouldLoad = this.getFinalOffset() <= this.getLoadedItemLookahead('before');
    } finally {
      this.edgeBusy = false;
    }
    if (shouldLoad) await this.loadMore('before');
  };

  handleNearBottom = async () => {
    if (this.edgeBusy) return;

    this.edgeBusy = true;
    let shouldLoad = false;

    try {
      const { items = [] } = this.props;
      const stepSize = this.getStepSize();

      if (this.getFinalOffset() < this.getMaxFinalOffset()) {
        await this.setLocalOffset(this.state.localOffset + stepSize);
      }

      const remaining = Math.max(0, items.length - (
        this.getFinalOffset() + this.props.windowSize
      ));
      shouldLoad = remaining <= this.getLoadedItemLookahead('after');
    } finally {
      this.edgeBusy = false;
    }
    if (shouldLoad) await this.loadMore('after');
  };

  removeBottomLock = () => {
    if (this.viewport) {
      this.viewport.removeBottomLock();
    }
  };

  scrollToTop = async () => {
    if (!this.viewport) return false;

    await this.setLocalOffset(-this.props.baseOffset);
    await nextAnimationFrame();

    this.viewport.scrollToTop();
    return true;
  };

  scrollToBottom = async () => {
    if (!this.viewport) return false;

    const finalOffset = this.getMaxFinalOffset();
    await this.setLocalOffset(finalOffset - this.props.baseOffset);
    await nextAnimationFrame();

    this.viewport.scrollToBottom();
    return true;
  };

  scrollToItem = async (item, userOptions = {}, then) => {
    if (!item || !this.viewport) {
      return false;
    }

    const {
      items = [],
      getId,
      getSelector,
      windowSize,
      baseOffset = 0,
      debug,
    } = this.props;

    const id = getId(item);
    const targetIndex = items.findIndex(candidate => getId(candidate) === id);

    if (targetIndex < 0) {
      if (debug) {
        console.warn('[VirtualizedView] Item not found:', id);
      }

      return false;
    }

    const sequence = ++this.scrollSequence;
    const wasCancelled = () => sequence !== this.scrollSequence;

    if (!this.isIndexRendered(targetIndex)) {
      const desiredFinalOffset = clamp(
        targetIndex - Math.floor(windowSize / 2),
        0,
        this.getMaxFinalOffset(),
      );

      await this.setLocalOffset(desiredFinalOffset - baseOffset);
      await nextAnimationFrame();
    }
    if (then) {
      then()
    }
    if (wasCancelled() || !this.viewport) {
      return false;
    }

    const element = this.viewport.findItemElement(id);

    if (!element) {
      if (debug) {
        console.warn(
          '[VirtualizedView] Item is in range but no element matched:',
          getSelector(id),
        );
      }

      return false;
    }

    const options = {
      behavior: 'smooth',
      block: 'nearest',
      inline: 'nearest',
      ...userOptions
    };

    await this.viewport.scrollIntoView(element, options, wasCancelled);
    return !wasCancelled();
  };

  scrollToTopItem = () => {
    const firstItem = this.props.items && this.props.items[0];
    return this.scrollToItem(firstItem, {
      behavior: 'smooth',
      block: 'start',
      inline: 'nearest',
    });
  };

  scrollToBottomItem = () => {
    const items = this.props.items || [];
    const lastItem = items[items.length - 1];

    return this.scrollToItem(lastItem, {
      behavior: 'smooth',
      block: 'end',
      inline: 'nearest',
    });
  };

  setScrollPos = async ({ offset = 0, scrollTop = 0 } = {}) => {
    await this.setLocalOffset(offset);
    await nextAnimationFrame();

    if (this.viewport) {
      this.viewport.setScrollTop(scrollTop);
      return true;
    }

    return false;
  };

  setScrollTop = scrollTop => {
    if (!this.viewport) return false;
    return this.viewport.setScrollTop(scrollTop);
  };

  onScroll = scrollTop => {
    if (!this.props.onScroll) return;

    this.props.onScroll({
      offset: this.state.localOffset,
      scrollTop,
    });
  };

  render() {
    const {
      items = [],
      getSelector,
      getId,
      renderItems,
      debug,
    } = this.props;

    const { start, end, finalOffset } = this.getRenderRange();
    const visibleItems = items.slice(start, end);
    /*
     * The bottom lock may engage only when the rendered slice actually
     * contains the list end — scrolling to the pixel bottom of a window
     * that stops short of the tail is not "at the bottom".
     */
    const canBottomLock = end >= items.length;

    if (debug) {
      console.log('[VirtualizedView] render', {
        finalOffset,
        renderStart: start,
        renderEnd: end,
        renderedCount: visibleItems.length,
        totalCount: items.length,
      });
    }

    return (
      <Viewport
        onScroll={this.props.onScroll ? this.onScroll : undefined}
        onBottomLockChanged={this.props.onBottomLockChanged}
        isBottomAligned={this.props.isBottomAligned}
        onKeyDown={this.props.onKeyDown}
        autoFocus={this.props.autoFocus}
        onCreate={this.onCreateViewport}
        onNearBottom={this.handleNearBottom}
        onNearTop={this.handleNearTop}
        edgeLookaheadScreens={this.props.edgeLookaheadScreens}
        maxEdgeLookaheadScreens={this.props.maxEdgeLookaheadScreens}
        refreshSafariCompositorOnWindowSwap={this.props.refreshSafariCompositorOnWindowSwap}
        getLoadLatencyMs={this.getLoadLatencyMs}
        canBottomLock={canBottomLock}
        items={visibleItems}
        getSelector={getSelector}
        renderItems={renderItems}
        getId={getId}
        debug={debug}
      />
    );
  }
}

export default VirtualizedView;
